import { Container, Sprite, type Texture } from 'pixi.js';
import type { EnemyState } from '@/combat/enemies';
import { type EnemySpriteKey, enemySpriteKey } from '@/sprites/enemies';

/**
 * Enemies on screen.
 *
 * Pooled, like every other renderer here. A hit flashes the sprite white for a
 * few frames: without it a swing that connects and a swing that misses look
 * identical, and the player learns nothing about their own reach.
 */
export class EnemyRenderer {
  readonly container = new Container();
  private readonly pool: Sprite[] = [];

  constructor(private readonly textures: Readonly<Record<EnemySpriteKey, Texture>>) {}

  render(enemies: readonly EnemyState[], frame: 0 | 1): void {
    let used = 0;

    for (const enemy of enemies) {
      const sprite = this.spriteAt(used++);
      sprite.texture = this.textures[enemySpriteKey(enemy.id, frame)];
      sprite.position.set(Math.round(enemy.x - 8), Math.round(enemy.y - 8));
      sprite.tint = enemy.flash > 0 ? 0xffffff : 0xcfcfcf;
      sprite.alpha = enemy.flash > 0 ? 1 : 0.98;
      sprite.visible = true;
    }

    for (let i = used; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  private spriteAt(index: number): Sprite {
    let sprite = this.pool[index];
    if (!sprite) {
      sprite = new Sprite();
      this.pool.push(sprite);
      this.container.addChild(sprite);
    }
    return sprite;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
